import { useEffect, useMemo } from "react";
import { Formik, Form, Field, useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { ArrowLeftIcon } from "@heroicons/react/outline";

import Button from "components/lib/Shared/Button";
import Input from "components/lib/Shared/Input";
import Card from "./Card";
import PaymentSummary from "./PaymentSummary";
import { BillingDetails } from "./BillingDetails";
import Radio from "components/lib/Shared/Radio";
import { getCompletePackageName } from "utils";
import { validationSchema } from "./validationSchema";
import { Constants } from "config/constants";

function PaymentMethodEffects({ selectedCurrencyType, isMpesaAllowed }) {
  const { values, setTouched, setFieldValue } = useFormikContext();

  useEffect(() => {
    setTouched({}, false);
  }, [values.paymentMethod, setTouched]);

  useEffect(() => {
    const nextPaymentMethod =
      selectedCurrencyType === "USD" || !isMpesaAllowed ? "card" : "mpesa";

    if (values.paymentMethod !== nextPaymentMethod) {
      setFieldValue("paymentMethod", nextPaymentMethod);
    }
  }, [
    selectedCurrencyType,
    isMpesaAllowed,
    values.paymentMethod,
    setFieldValue,
  ]);

  return null;
}

function PaymentMethodOptions({
  paymentMethod,
  setFieldValue,
  isSubmitting,
  selectedCurrencyType,
  isMpesaAllowed,
}) {
  return (
    <div className="flex my-4 space-x-8">
      <div className="flex items-center">
        <Radio
          id="mpesa"
          name="paymentMethod"
          label="Mpesa"
          type="radio"
          disabled={
            isSubmitting || selectedCurrencyType === "USD" || !isMpesaAllowed
          }
          checked={paymentMethod === "mpesa"}
          onChange={() => setFieldValue("paymentMethod", "mpesa")}
          className="h-4 w-4 border-gray-300 text-sflPrimary focus:ring-sflPrimary"
        />
      </div>

      <div className="flex items-center">
        <Radio
          id="card"
          name="paymentMethod"
          label="Card"
          type="radio"
          disabled={isSubmitting}
          checked={paymentMethod === "card"}
          onChange={() => setFieldValue("paymentMethod", "card")}
          className="h-4 w-4 border-gray-300 text-sflPrimary focus:ring-sflPrimary"
        />
      </div>

      <div className="flex items-center">
        <Radio
          id="eWallet"
          name="paymentMethod"
          label="E-Wallet"
          type="radio"
          disabled
          checked={paymentMethod === "eWallet"}
          onChange={() => setFieldValue("paymentMethod", "eWallet")}
          className="h-4 w-4 border-gray-300 text-sflPrimary focus:ring-sflPrimary"
        />
      </div>
    </div>
  );
}

function PaymentMethodFormContent({
  companyType,
  selectedPackage,
  selectedCurrency,
  actualAmount,
  discountAmount,
  paymentResponse,
  transactionToken,
  isSubmitting,
  isRetrying,
  canRetryPayment,
  handleGoBack,
  handleCancelTransaction,
  handleRetryPayment,
  isMpesaAllowed,
}) {
  const { values, setFieldValue } = useFormikContext();
  const { paymentMethod, expiryDate } = values;

  return (
    <Form>
      <PaymentMethodEffects
        selectedCurrencyType={selectedCurrency.type}
        isMpesaAllowed={isMpesaAllowed}
      />

      <div className="grid grid-cols-12 gap-4 Wrapper text-primary">
        <div className="col-span-8">
          <div className="space-y-1 text-sm">
            <h3>
              You have selected to set up a <b>{companyType}</b> with the{" "}
              <b>{getCompletePackageName(selectedPackage)}</b> package.
            </h3>
          </div>

          <div>
            <div className="py-3">
              <h1 className="font-medium">Step: 1 of 2: Billing details</h1>
              <hr className="my-1" />
            </div>
            <BillingDetails />
          </div>

          <div className="my-4 text-sm">
            <div>
              <h1 className="font-medium">
                Step: 2 of 2: Select your default payment option
              </h1>
              <hr className="my-1" />
              <p>
                Select your preferred default payment method, which will be used
                at checkout for future purchases. We accept mpesa, card payment.
              </p>
            </div>

            <PaymentMethodOptions
              paymentMethod={paymentMethod}
              setFieldValue={setFieldValue}
              isSubmitting={isSubmitting}
              selectedCurrencyType={selectedCurrency.type}
              isMpesaAllowed={isMpesaAllowed}
            />

            <div className="p-4 space-y-4 bg-white rounded-md shadow-sm drop-shadow-md">
              {paymentMethod === "mpesa" && (
                <div className="flex">
                  <Field
                    component={Input}
                    showRequiredAsterisk
                    name="mpesaPhoneNumber"
                    label="Mpesa phone number"
                    placeholder="254123456789"
                  />
                </div>
              )}

              {paymentMethod === "card" && <Card expiryDate={expiryDate} />}

              <p className="text-center text-sm text-gray-500">
                Your transaction is secured with SSL encryption
              </p>
            </div>

            <p className="text-center text-sm mt-2 text-gray-500">
              Payments are VAT inclusive and there will be no additional charges
              on your invoice.
            </p>

            {paymentResponse && !canRetryPayment && (
              <div className="rounded-md bg-light-blue/10 p-4 mt-2">
                <div className="flex">
                  <div className="ml-3 flex-1 md:flex md:justify-between">
                    <p className="text-sm text-light-blue">{paymentResponse}</p>
                  </div>
                </div>
              </div>
            )}

            {canRetryPayment && (
              <div className="rounded-md bg-light-blue/10 p-4 mt-2">
                <div className="flex">
                  <div className="ml-3 flex-1 md:flex md:justify-between">
                    <p className="text-sm text-light-blue">
                      If you have already made the payment, click on the retry
                      button
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex space-x-2">
              {isSubmitting && transactionToken && (
                <Button
                  className="flex items-center px-4 py-2 font-semibold text-white rounded bg-sflPrimary mt-7 w-1/4 justify-center"
                  onClick={handleCancelTransaction}
                >
                  Cancel
                </Button>
              )}

              {!isRetrying && (
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  loadingText="Processing..."
                  className="flex items-center w-1/4 px-4 py-2 font-semibold text-white rounded bg-sflPrimary mt-7 justify-center"
                >
                  <p className="font-semibold">Make payment</p>
                  <span className="flex items-center ml-2 text-white h-7" />
                </Button>
              )}

              {canRetryPayment && (
                <Button
                  isLoading={isRetrying}
                  onClick={handleRetryPayment}
                  loadingText="Retrying..."
                  className="flex items-center w-1/4 px-4 py-2 font-semibold text-white rounded bg-sflPrimary mt-7 justify-center"
                >
                  <p className="font-semibold">Retry payment</p>
                  <span className="flex items-center ml-2 text-white h-7" />
                </Button>
              )}
            </div>
          </div>
        </div>

        <PaymentSummary
          selectedCurrencyType={selectedCurrency.type}
          actualAmount={actualAmount}
          discountAmount={discountAmount}
        />
      </div>
    </Form>
  );
}

function PaymentMethod({
  companyType,
  selectedPackage,
  discountPercentage,
  selectedCurrency,
  paymentResponse,
  transactionToken,
  isSubmitting,
  isRetrying,
  canRetryPayment,
  handleMakePayment,
  handleGoBack,
  handleCancelTransaction,
  handleRetryPayment,
}) {
  const userSlice = useSelector((state) => state.userSlice);

  const actualAmount = useMemo(() => {
    return (
      (selectedCurrency.basePrice + (selectedCurrency?.profitMargin ?? 0)) *
      selectedCurrency.applicableTax
    );
  }, [selectedCurrency]);

  const discountAmount = useMemo(() => {
    return discountPercentage ? actualAmount * (discountPercentage / 100) : 0;
  }, [actualAmount, discountPercentage]);

  const totalAmount = useMemo(() => {
    return actualAmount - discountAmount;
  }, [actualAmount, discountAmount]);

  const isMpesaAllowed = totalAmount <= Constants.MPESA_LIMIT.MAX_AMOUNT;

  const initialValues = useMemo(
    () => ({
      firstName: userSlice?.names?.firstName ?? "",
      lastName: userSlice?.names?.lastName ?? "",
      email: userSlice?.email ?? "",
      mobileNumber: userSlice?.electronicAddress?.mobileNumber ?? "",
      billingAddress: [
        "Kenyan Citizen",
        "Foreign Resident",
        "Local Company",
      ].includes(userSlice.idType)
        ? `PO BOX ${userSlice?.postalAddress?.postalAddress} ${userSlice?.postalAddress?.postalCode}`
        : userSlice?.postalAddress?.postalAddress,
      paymentMethod: "mpesa",
      mpesaPhoneNumber:
        userSlice?.mpesaPhoneNumber ??
        userSlice?.electronicAddress?.mobileNumber?.replace("+", "") ??
        "",
      cardHolderName: "",
      cardNumber: "",
      expiryDate: "",
      cvv: "",
    }),
    [userSlice]
  );

  return (
    <>
      <Button
        onClick={() => handleGoBack(0)}
        variant="text"
        className="flex space-x-2 text-sm font-medium rounded-md group text-gray-600 mb-2"
      >
        <ArrowLeftIcon
          className="w-5 h-5 text-gray-400 group-hover:text-gray-800"
          aria-hidden="true"
        />
        <span className="group-hover:text-gray-800">Back</span>
      </Button>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleMakePayment(actualAmount, discountAmount, values);
        }}
      >
        <PaymentMethodFormContent
          companyType={companyType}
          selectedPackage={selectedPackage}
          selectedCurrency={selectedCurrency}
          actualAmount={actualAmount}
          discountAmount={discountAmount}
          paymentResponse={paymentResponse}
          transactionToken={transactionToken}
          isSubmitting={isSubmitting}
          isRetrying={isRetrying}
          canRetryPayment={canRetryPayment}
          handleGoBack={handleGoBack}
          handleCancelTransaction={handleCancelTransaction}
          handleRetryPayment={handleRetryPayment}
          isMpesaAllowed={isMpesaAllowed}
        />
      </Formik>
    </>
  );
}

export default PaymentMethod;
